<!-- src/apps/secret/views/legal/TermsView.vue -->

<script setup lang="ts">
  /**
   * Termos de Uso do SeuID.
   *
   * Mesma decisão da PrivacyView: conteúdo jurídico longo inline em pt_BR
   * (documento único, revisado como um todo), não fatiado em chaves i18n.
   * Versão en resumida pode ser adicionada depois.
   */
  const LAST_UPDATED = '11 de agosto de 2026';

  const sections = [
    { id: 'servico', n: '01', title: 'O serviço' },
    { id: 'aceitacao', n: '02', title: 'Aceitação e cadastro' },
    { id: 'uso-aceitavel', n: '03', title: 'Uso aceitável' },
    { id: 'efemero', n: '04', title: 'Natureza efêmera do conteúdo' },
    { id: 'disponibilidade', n: '05', title: 'Disponibilidade e garantias' },
    { id: 'responsabilidade', n: '06', title: 'Limitação de responsabilidade' },
    { id: 'propriedade', n: '07', title: 'Propriedade intelectual' },
    { id: 'rescisao', n: '08', title: 'Alterações e rescisão' },
    { id: 'lei-foro', n: '09', title: 'Lei aplicável e foro' },
  ];
</script>

<template>
  <article class="mx-auto w-full max-w-3xl px-5 pb-16 sm:px-8">
    <!-- Back link -->
    <router-link
      to="/"
      class="inline-flex items-center gap-2 pt-2 font-mono text-[11px] tracking-[0.08em] text-day-ink-faint
        uppercase transition-colors hover:text-brand-600
        dark:text-night-ink-faint dark:hover:text-brand-500">
      <span aria-hidden="true">&larr;</span>
      voltar ao início
    </router-link>

    <!-- Header -->
    <header class="mt-8 border-b border-day-line pb-8 dark:border-white/[0.07]">
      <p class="mb-3 font-mono text-[10px] tracking-[0.1em] text-brand-600 uppercase dark:text-brand-500">
        documento legal
      </p>
      <h1 class="font-brand text-[34px] leading-[1.15] font-bold text-day-ink sm:text-[40px] dark:text-night-ink">
        Termos de Uso
      </h1>
      <p class="mt-4 font-mono text-[11px] tracking-[0.04em] text-day-ink-faint dark:text-night-ink-faint">
        Última atualização: {{ LAST_UPDATED }}
      </p>
      <p
        class="mt-5 border border-day-line bg-day-100 px-4 py-3 text-[13px] leading-[1.65] font-light
          text-day-ink-muted dark:border-white/[0.07] dark:bg-night-100 dark:text-night-ink-muted">
        Estes termos regem o uso do SeuID (seuid.com.br). Ao usar o serviço, você concorda com
        eles. O texto pode ser revisado periodicamente; a data acima indica a versão vigente.
      </p>
    </header>

    <!-- Index -->
    <nav
      aria-label="Sumário"
      class="border-b border-day-line py-6 dark:border-white/[0.07]">
      <ol class="grid grid-cols-1 gap-x-8 gap-y-1.5 sm:grid-cols-2">
        <li
          v-for="s in sections"
          :key="s.id">
          <a
            :href="`#${s.id}`"
            class="group inline-flex items-baseline gap-2 text-[13px] text-day-ink-muted
              transition-colors hover:text-brand-600 dark:text-night-ink-muted dark:hover:text-brand-500">
            <span class="font-mono text-[10px] text-brand-600 dark:text-brand-500">{{ s.n }}.</span>
            {{ s.title }}
          </a>
        </li>
      </ol>
    </nav>

    <div
      class="space-y-10 pt-10 text-[15px] leading-[1.75] font-light text-day-ink-muted dark:text-night-ink-muted
        [&_h2]:mb-3 [&_h2]:flex [&_h2]:items-baseline [&_h2]:gap-3 [&_h2]:font-brand [&_h2]:text-[22px]
        [&_h2]:leading-snug [&_h2]:font-semibold [&_h2]:text-day-ink dark:[&_h2]:text-night-ink
        [&_ul]:list-disc [&_ul]:space-y-1.5 [&_ul]:pl-5">
      <section
        id="servico"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">01.</span>
          O serviço
        </h2>
        <p>
          O SeuID é um serviço de compartilhamento de mensagens de uso único: você escreve um
          conteúdo sensível (uma senha, uma chave, um recado), o serviço o cifra e gera um link
          que só pode ser aberto <strong>uma única vez</strong>. Na primeira leitura — ou ao
          expirar o prazo definido — o conteúdo é destruído de forma definitiva.
        </p>
        <p class="mt-3">
          Opcionalmente, você pode <strong>anexar arquivos</strong> a um segredo. Os anexos são
          <strong>cifrados de ponta a ponta no seu navegador</strong>: a chave viaja apenas no
          link e o SeuID <strong>não tem acesso ao conteúdo</strong> do arquivo. Aplicam-se
          limites técnicos (por exemplo, tamanho máximo por arquivo e quantidade por segredo
          conforme o seu plano).
        </p>
      </section>

      <section
        id="aceitacao"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">02.</span>
          Aceitação e cadastro
        </h2>
        <ul>
          <li>
            Ao acessar ou usar o SeuID, você declara ter lido e aceito estes termos e a
            <router-link
              to="/privacidade"
              class="text-brand-600 underline underline-offset-2 hover:no-underline dark:text-brand-500">
              Política de Privacidade
            </router-link>, e ter capacidade civil para contratar.
          </li>
          <li>
            Algumas funcionalidades exigem conta, criada com e-mail válido e verificado. Você é
            responsável por manter a confidencialidade da sua senha e por toda atividade
            realizada com as suas credenciais.
          </li>
          <li>
            As informações fornecidas no cadastro devem ser verdadeiras e mantidas atualizadas.
          </li>
        </ul>
      </section>

      <section
        id="uso-aceitavel"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">03.</span>
          Uso aceitável
        </h2>
        <p>É proibido usar o SeuID para:</p>
        <ul class="mt-2">
          <li>compartilhar conteúdo ilícito ou praticar qualquer atividade contrária à lei;</li>
          <li>phishing, engenharia social ou distribuição de malware;</li>
          <li>spam ou envio em massa não solicitado;</li>
          <li>
            tratar dados pessoais de terceiros sem base legal, em violação à LGPD ou a outras
            normas de proteção de dados;
          </li>
          <li>
            tentar burlar limites técnicos, sondar vulnerabilidades ou interferir na operação
            do serviço.
          </li>
        </ul>
        <p class="mt-3">
          O SeuID pode <strong>suspender ou encerrar contas</strong> e remover conteúdo que
          viole estes termos, sem prejuízo das medidas legais cabíveis.
        </p>
        <p class="mt-3">
          <strong>Anexos e responsabilidade pelo conteúdo.</strong> As proibições acima
          aplicam-se integralmente aos arquivos anexados. Como os anexos são
          <strong>cifrados de ponta a ponta</strong>, o SeuID
          <strong>não consegue inspecionar, moderar ou pré-filtrar</strong> o seu conteúdo — a
          responsabilidade por respeitar a lei e os direitos de terceiros é
          <strong>integralmente sua</strong>. Você declara ter os direitos necessários sobre
          os arquivos que anexa e concorda em não anexar conteúdo ilícito.
        </p>
      </section>

      <section
        id="efemero"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">04.</span>
          Natureza efêmera do conteúdo
        </h2>
        <ul>
          <li>
            Uma vez lido ou expirado, o conteúdo de um segredo é
            <strong>irrecuperável</strong> — o SeuID não mantém cópia, backup nem histórico.
          </li>
          <li>
            É sua responsabilidade confirmar com o destinatário que a mensagem foi recebida
            antes de descartar o original.
          </li>
          <li>
            O link de um segredo dá acesso ao conteúdo a quem o possuir (respeitada a
            passphrase opcional). Trate o link com o mesmo cuidado que o próprio conteúdo.
          </li>
          <li>
            <strong>Anexos:</strong> o arquivo é apagado após o download ou a queima do
            segredo. Como a chave de decifração existe <strong>apenas no link</strong>, se
            você perder o link o anexo é <strong>irrecuperável</strong> — o SeuID não tem a
            chave nem cópia do conteúdo.
          </li>
        </ul>
      </section>

      <section
        id="disponibilidade"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">05.</span>
          Disponibilidade e garantias
        </h2>
        <p>
          O SeuID é fornecido "no estado em que se encontra", sem garantia de disponibilidade
          ininterrupta ou livre de erros, dentro dos limites permitidos pela legislação
          brasileira — em especial o Código de Defesa do Consumidor (Lei nº 8.078/1990), cujas
          garantias legais permanecem integralmente aplicáveis quando você usa o serviço como
          consumidor. Podemos realizar manutenções, atualizações e melhorias que interrompam
          temporariamente o serviço, buscando sempre minimizar o impacto.
        </p>
      </section>

      <section
        id="responsabilidade"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">06.</span>
          Limitação de responsabilidade
        </h2>
        <p>
          Na máxima extensão permitida pela lei brasileira, o SeuID não responde por danos
          indiretos, lucros cessantes ou perda de dados decorrentes (i) do uso do serviço em
          desacordo com estes termos, (ii) da destruição de conteúdo inerente ao funcionamento
          de uso único do serviço, ou (iii) de caso fortuito e força maior. Nada nestes termos
          exclui ou limita responsabilidades que não possam ser excluídas ou limitadas por lei,
          incluindo as previstas no Código de Defesa do Consumidor.
        </p>
      </section>

      <section
        id="propriedade"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">07.</span>
          Propriedade intelectual
        </h2>
        <p>
          A marca SeuID, o nome de domínio seuid.com.br, o logotipo e a identidade visual do
          serviço são de titularidade do SeuID e não podem ser usados sem autorização prévia e
          expressa. O conteúdo que você compartilha pelo serviço continua sendo seu — o SeuID
          não reivindica qualquer direito sobre ele.
        </p>
      </section>

      <section
        id="rescisao"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">08.</span>
          Alterações e rescisão
        </h2>
        <ul>
          <li>
            Podemos atualizar estes termos; a versão vigente é a publicada nesta página, com a
            data no topo. Alterações relevantes serão comunicadas pelos canais do serviço, e o
            uso continuado após a comunicação constitui aceitação.
          </li>
          <li>
            Você pode encerrar sua conta a qualquer momento pelo canal de suporte
            (<a
              href="mailto:suporte@seuid.com.br"
              class="text-brand-600 underline underline-offset-2 hover:no-underline dark:text-brand-500">suporte@seuid.com.br</a>).
          </li>
          <li>
            O SeuID pode suspender ou encerrar o serviço ou contas em caso de violação destes
            termos, mediante comunicação quando possível.
          </li>
        </ul>
      </section>

      <section
        id="lei-foro"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">09.</span>
          Lei aplicável e foro
        </h2>
        <p>
          Estes termos são regidos pelas leis da República Federativa do Brasil. Fica eleito o
          foro do domicílio do usuário, quando consumidor, ou, nos demais casos, o foro da
          comarca de São Paulo/SP, para dirimir quaisquer controvérsias.
        </p>
      </section>
    </div>

    <!-- Cross-link -->
    <footer class="mt-12 border-t border-day-line pt-6 dark:border-white/[0.07]">
      <router-link
        to="/privacidade"
        class="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.08em] text-brand-600
          uppercase transition-colors hover:text-brand-700 dark:text-brand-500 dark:hover:text-brand-400">
        Política de Privacidade
        <span aria-hidden="true">&rarr;</span>
      </router-link>
    </footer>
  </article>
</template>
